import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Label,
    Pie,
    PieChart,
    XAxis,
    YAxis,
} from 'recharts';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import {
    SignalIcon,
    UserGroupIcon,
    ComputerDesktopIcon,
} from '@heroicons/react/24/outline';
import {
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from '../../components/ui/chart';
import { PeriodCompactFilter } from './CompactFilterMenu';
import { cn } from '../../lib/utils';
import {
    CHART_ACCENT,
    CHART_MUTED,
    DEFAULT_CHART_PERIOD,
    DEVICE_CHART_COLORS,
    MEXICO_TZ,
    ROLE_CHART_COLORS,
} from './usuariosOnline.constants';

const usePrefersReducedMotion = () => {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const media = window.matchMedia('(prefers-reduced-motion: reduce)');
        const sync = () => setReduced(media.matches);
        sync();
        media.addEventListener?.('change', sync);
        return () => media.removeEventListener?.('change', sync);
    }, []);
    return reduced;
};

const cardClass =
    'flex h-full flex-col rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm';

const MetricSkeleton = ({ className = 'h-8 w-16' }) => (
    <div className={`rounded-md bg-slate-200/90 animate-blink ${className}`} aria-hidden />
);

const EmptyChart = ({ message = 'Sin datos para este periodo.' }) => (
    <div className='flex flex-1 items-center justify-center px-2 py-4 text-center text-sm text-slate-400'>
        {message}
    </div>
);

/** Ticks enteros para ejes Y (0…n o pasos regulares). */
const buildIntegerTicks = (maxValue, preferredSteps = 4) => {
    const yMax = Math.max(1, Number(maxValue) || 0);
    if (yMax <= 4) return Array.from({ length: yMax + 1 }, (_, i) => i);
    const step = Math.ceil(yMax / preferredSteps);
    const ticks = [];
    for (let value = 0; value < yMax; value += step) ticks.push(value);
    if (ticks[ticks.length - 1] !== yMax) ticks.push(yMax);
    return ticks;
};

const KpiStat = ({
    label,
    value,
    hint,
    loading,
    icon: Icon,
    valueClassName = 'text-4xl',
    className = '',
}) => (
    <div className={`flex min-h-0 flex-col justify-between ${className}`.trim()}>
        <div className='flex items-start justify-between gap-2'>
            <p className='text-base font-bold text-slate-900'>{label}</p>
            <span className='inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-slate-200/80 bg-slate-50 text-slate-500'>
                <Icon className='size-4' aria-hidden='true' />
            </span>
        </div>
        <div className='mt-2'>
            {loading ? (
                <MetricSkeleton className='h-10 w-20' />
            ) : (
                <p className={`font-bold tracking-tight tabular-nums text-slate-950 ${valueClassName}`}>
                    {Number(value || 0).toLocaleString('es-MX')}
                </p>
            )}
            {hint ? (
                <p className='mt-1 text-xs text-slate-400'>{hint}</p>
            ) : null}
        </div>
    </div>
);

const LIVE_SERIES_MAX = 24;
const LIVE_SAMPLE_MS = 12_000;

const liveChartConfig = {
    online: { label: 'Online', color: CHART_ACCENT },
};

const formatLiveTick = (timestamp) => {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: MEXICO_TZ,
    });
};

/** Serie en vivo: muestrea usuarios online ante cada cambio y cada ~12s. */
const useLiveOnlineSeries = (currentlyOnline, loading) => {
    const [series, setSeries] = useState([]);
    const valueRef = useRef(0);
    valueRef.current = Number(currentlyOnline || 0);

    const pushSample = (value) => {
        setSeries((prev) => {
            const now = Date.now();
            const last = prev[prev.length - 1];
            if (last && now - last.t < 2_000 && last.online === value) return prev;
            return [...prev, { t: now, online: value }].slice(-LIVE_SERIES_MAX);
        });
    };

    useEffect(() => {
        if (loading) return undefined;
        pushSample(valueRef.current);
        return undefined;
    }, [currentlyOnline, loading]);

    useEffect(() => {
        if (loading) return undefined;
        const id = window.setInterval(() => {
            pushSample(valueRef.current);
        }, LIVE_SAMPLE_MS);
        return () => window.clearInterval(id);
    }, [loading]);

    return series;
};

const LiveOnlineSparkline = ({ series, loading, reduceMotion }) => {
    const chartData = useMemo(
        () => series.map((point) => ({
            ...point,
            label: formatLiveTick(point.t),
        })),
        [series],
    );
    const yMax = useMemo(() => {
        const peak = chartData.reduce((max, point) => Math.max(max, point.online || 0), 0);
        return Math.max(1, peak);
    }, [chartData]);
    const yTicks = useMemo(() => buildIntegerTicks(yMax, 3), [yMax]);

    if (loading) {
        return (
            <div className='mt-2 flex min-h-[160px] flex-1 flex-col border-t border-slate-100 pt-2 md:min-h-0'>
                <div className='relative min-h-[140px] flex-1 md:min-h-0'>
                    <MetricSkeleton className='absolute inset-0 rounded-lg' />
                </div>
            </div>
        );
    }

    if (chartData.length < 2) {
        return (
            <div className='mt-2 flex min-h-[160px] flex-1 flex-col border-t border-slate-100 pt-2 md:min-h-0'>
                <div className='relative min-h-[140px] flex-1 md:min-h-0'>
                    <div className='absolute inset-0 flex items-center rounded-lg border border-dashed border-slate-200 bg-slate-50/80 px-3 text-[11px] text-slate-400'>
                        El gráfico en vivo aparecerá con las siguientes actualizaciones…
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='mt-2 flex min-h-[160px] flex-1 flex-col border-t border-slate-100 pt-2 md:min-h-0'>
            <div className='mb-0.5 flex shrink-0 items-center justify-between gap-2'>
                <p className='text-[11px] font-medium text-slate-400'>Presencia en vivo</p>
                <span className='inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-600'>
                    <span className='relative flex size-1.5'>
                        {!reduceMotion ? (
                            <span className='absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-60' />
                        ) : null}
                        <span className='relative inline-flex size-1.5 rounded-full bg-emerald-500' />
                    </span>
                    Live
                </span>
            </div>
            <div className='relative min-h-[140px] min-w-0 flex-1 md:min-h-0'>
                <ChartContainer
                    config={liveChartConfig}
                    className='absolute inset-0 h-full w-full aspect-auto'
                >
                    <AreaChart data={chartData} margin={{ top: 8, right: 4, left: 0, bottom: 2 }}>
                        <defs>
                            <linearGradient id='liveOnlineFill' x1='0' y1='0' x2='0' y2='1'>
                                <stop offset='0%' stopColor={CHART_ACCENT} stopOpacity={0.35} />
                                <stop offset='100%' stopColor={CHART_ACCENT} stopOpacity={0.02} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} strokeDasharray='4 4' stroke='#e2e8f0' />
                        <XAxis
                            dataKey='label'
                            tickLine={false}
                            axisLine={false}
                            tickMargin={4}
                            minTickGap={28}
                            interval='preserveStartEnd'
                            tick={{ fontSize: 10, fill: '#64748b' }}
                        />
                        <YAxis
                            allowDecimals={false}
                            tickLine={false}
                            axisLine={false}
                            width={22}
                            domain={[0, yMax]}
                            ticks={yTicks}
                            interval={0}
                            minTickGap={0}
                            tick={{ fontSize: 10, fill: '#64748b' }}
                        />
                        <ChartTooltip
                            cursor={{ stroke: CHART_ACCENT, strokeWidth: 1, strokeDasharray: '3 3' }}
                            content={<ChartTooltipContent indicator='line' />}
                            position={{ y: 8 }}
                        />
                        <Area
                            type='monotone'
                            dataKey='online'
                            name='Online'
                            stroke={CHART_ACCENT}
                            strokeWidth={2}
                            fill='url(#liveOnlineFill)'
                            isAnimationActive={!reduceMotion}
                            animationDuration={400}
                            dot={false}
                            activeDot={{ r: 3, fill: CHART_ACCENT }}
                        />
                    </AreaChart>
                </ChartContainer>
            </div>
        </div>
    );
};

const ActivityKpis = ({ metrics, loading, liveSeries, reduceMotion }) => (
    <div className={cn(cardClass, 'pb-2')}>
        <div className='grid h-full min-h-0 grid-cols-1 gap-4 md:grid-cols-2 md:gap-0'>
            <div className='flex min-h-0 flex-1 flex-col md:border-r md:border-slate-100 md:pr-4'>
                <KpiStat
                    label='Usuarios online'
                    value={metrics?.currentlyOnline}
                    hint='Conectados en este momento'
                    loading={loading}
                    icon={SignalIcon}
                    valueClassName='text-5xl sm:text-6xl'
                    className='shrink-0'
                />
                <LiveOnlineSparkline
                    series={liveSeries}
                    loading={loading}
                    reduceMotion={reduceMotion}
                />
            </div>
            <div className='grid grid-cols-1 gap-4 md:pl-4'>
                <KpiStat
                    label='Usuarios únicos hoy'
                    value={metrics?.uniqueConnectedToday}
                    hint='Alcance de hoy · tiempo real'
                    loading={loading}
                    icon={UserGroupIcon}
                    valueClassName='text-4xl sm:text-5xl'
                    className='md:border-b md:border-slate-100 md:pb-4'
                />
                <KpiStat
                    label='Sesiones activas'
                    value={metrics?.activeSessions}
                    hint='Pestañas o dispositivos abiertos'
                    loading={loading}
                    icon={ComputerDesktopIcon}
                    valueClassName='text-4xl sm:text-5xl'
                />
            </div>
        </div>
    </div>
);

const roleChartConfig = {
    profesor: { label: 'Profesor', color: ROLE_CHART_COLORS.profesor },
    estudiante: { label: 'Estudiante', color: ROLE_CHART_COLORS.estudiante },
    administrativo: { label: 'Administrativo', color: ROLE_CHART_COLORS.administrativo },
};

const deviceChartConfig = {
    mobile: { label: 'Mobile', color: DEVICE_CHART_COLORS.mobile },
    desktop: { label: 'Desktop', color: DEVICE_CHART_COLORS.desktop },
};

const usersByDayConfig = {
    users: { label: 'Usuarios únicos', color: CHART_ACCENT },
};

const CenterDonutPercent = ({ viewBox, percent }) => {
    const cx = viewBox?.cx;
    const cy = viewBox?.cy;
    if (cx == null || cy == null) return null;

    return (
        <text
            x={cx}
            y={cy}
            textAnchor='middle'
            dominantBaseline='central'
            fill='#020617'
            style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.02em' }}
        >
            {percent}
        </text>
    );
};

const DonutCard = ({
    title,
    data,
    config,
    loading,
    emptyMessage,
    centerPercent,
    reduceMotion,
    period,
    onPeriodChange,
}) => {
    const total = data.reduce((sum, row) => sum + (row.value || 0), 0);

    return (
        <div className={`${cardClass} min-h-0`}>
            <div className='flex shrink-0 items-start justify-between gap-2'>
                <p className='min-w-0 text-base font-bold text-slate-900'>{title}</p>
                <PeriodCompactFilter value={period} onChange={onPeriodChange} disabled={loading} />
            </div>

            {loading ? (
                <div className='mt-3 flex flex-1 items-center gap-3'>
                    <MetricSkeleton className='size-20 shrink-0 rounded-full' />
                    <div className='flex flex-1 flex-col gap-2'>
                        <MetricSkeleton className='h-8 w-full' />
                        <MetricSkeleton className='h-8 w-full' />
                    </div>
                </div>
            ) : total === 0 ? (
                <EmptyChart message={emptyMessage} />
            ) : (
                <div className='mt-2 grid min-h-0 flex-1 grid-cols-12 items-center gap-3'>
                    <div className='col-span-4 mx-auto aspect-square w-full max-w-[96px] sm:mx-0 sm:max-w-[108px]'>
                        <ChartContainer config={config} className='aspect-square h-full w-full'>
                            <PieChart>
                                <ChartTooltip
                                    cursor={false}
                                    content={<ChartTooltipContent hideLabel nameKey='key' />}
                                />
                                <Pie
                                    data={data}
                                    dataKey='value'
                                    nameKey='key'
                                    innerRadius='58%'
                                    outerRadius='94%'
                                    paddingAngle={3}
                                    cornerRadius={8}
                                    strokeWidth={0}
                                    isAnimationActive={!reduceMotion}
                                    animationDuration={550}
                                >
                                    {data.map((entry) => (
                                        <Cell
                                            key={entry.key}
                                            fill={`var(--color-${entry.key})`}
                                        />
                                    ))}
                                    <Label
                                        content={(props) => (
                                            <CenterDonutPercent
                                                {...props}
                                                percent={centerPercent}
                                            />
                                        )}
                                    />
                                </Pie>
                            </PieChart>
                        </ChartContainer>
                    </div>

                    <ul
                        className={`col-span-8 grid min-w-0 gap-2 ${
                            data.length > 2 ? 'grid-cols-3' : 'grid-cols-2'
                        }`}
                    >
                        {data.map((row) => (
                            <li key={row.key} className='min-w-0 text-center'>
                                <p className='inline-flex max-w-full items-center justify-center gap-1.5 text-[11px] font-medium text-slate-500'>
                                    <span
                                        className='size-2 shrink-0 rounded-full'
                                        style={{ backgroundColor: config[row.key]?.color }}
                                        aria-hidden='true'
                                    />
                                    <span className='truncate'>
                                        {config[row.key]?.label || row.name}
                                    </span>
                                </p>
                                <p className='mt-0.5 text-3xl font-bold leading-none tracking-tight tabular-nums text-slate-950 sm:text-4xl'>
                                    {Number(row.value || 0).toLocaleString('es-MX')}
                                </p>
                                <p className='mt-0.5 truncate text-[10px] text-slate-400'>
                                    {row.percent}%
                                </p>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

const RoleDonutCard = ({
    metrics, loading, reduceMotion, period, onPeriodChange,
}) => {
    const data = useMemo(() => {
        const rows = metrics?.byRole?.length
            ? metrics.byRole
            : [
                { key: 'profesor', label: 'Profesor', value: 0 },
                { key: 'estudiante', label: 'Estudiante', value: 0 },
                { key: 'administrativo', label: 'Administrativo', value: 0 },
            ];
        const total = rows.reduce((sum, row) => sum + (row.value || 0), 0);
        return rows.map((row) => ({
            ...row,
            name: row.label,
            percent: total ? Math.round((row.value / total) * 100) : 0,
        }));
    }, [metrics]);

    const top = data.reduce((best, row) => (row.value > (best?.value || 0) ? row : best), null);

    return (
        <DonutCard
            title='Usuarios por tipo'
            data={data}
            config={roleChartConfig}
            loading={loading}
            emptyMessage='Sin usuarios en este periodo.'
            centerPercent={top ? `${top.percent}%` : '0%'}
            reduceMotion={reduceMotion}
            period={period}
            onPeriodChange={onPeriodChange}
        />
    );
};

const DeviceDonutCard = ({
    metrics, loading, reduceMotion, period, onPeriodChange,
}) => {
    const data = useMemo(() => {
        const mobile = metrics?.devices?.mobile ?? 0;
        const desktop = metrics?.devices?.desktop ?? 0;
        const total = mobile + desktop;
        return [
            {
                key: 'mobile',
                name: 'Mobile',
                value: mobile,
                percent: total ? Math.round((mobile / total) * 100) : 0,
            },
            {
                key: 'desktop',
                name: 'Desktop',
                value: desktop,
                percent: total ? Math.round((desktop / total) * 100) : 0,
            },
        ];
    }, [metrics]);

    const top = data.reduce((best, row) => (row.value > (best?.value || 0) ? row : best), null);

    return (
        <DonutCard
            title='Usuarios por móvil y desktop'
            data={data}
            config={deviceChartConfig}
            loading={loading}
            emptyMessage='Sin conexiones en este periodo.'
            centerPercent={top ? `${top.percent}%` : '0%'}
            reduceMotion={reduceMotion}
            period={period}
            onPeriodChange={onPeriodChange}
        />
    );
};

const StripedBar = (props) => {
    const {
        x, y, width, height, fill, payload, highlightDate, hoveredDate,
    } = props;
    if (height <= 0 || width <= 0) return null;

    const isHighlighted = payload?.date === highlightDate || payload?.date === hoveredDate;
    const radius = 6;
    const path = `
        M${x},${y + height}
        L${x},${y + radius}
        Q${x},${y} ${x + radius},${y}
        L${x + width - radius},${y}
        Q${x + width},${y} ${x + width},${y + radius}
        L${x + width},${y + height}
        Z
    `;

    if (isHighlighted) {
        return <path d={path} fill={fill || CHART_ACCENT} />;
    }

    return (
        <path d={path} fill='url(#usersBarHatch)' stroke={CHART_MUTED} strokeWidth={1} />
    );
};

const BAR_VISIBLE_DAYS = 7;

const mexicoTodayKey = () => (
    new Date().toLocaleDateString('en-CA', { timeZone: MEXICO_TZ })
);

const formatBarDayLabel = (dateKey) => {
    if (!dateKey) return '';
    const date = new Date(`${dateKey}T12:00:00`);
    if (Number.isNaN(date.getTime())) return dateKey;
    const parts = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: MEXICO_TZ,
    }).formatToParts(date);
    const get = (type) => parts.find((part) => part.type === type)?.value || '';
    return `${get('month')} ${get('day')} de ${get('year')}`;
};

const UniqueUsersTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    if (!row) return null;
    const users = Number(row.users || 0);

    return (
        <div className='min-w-[9.5rem] rounded-lg border border-slate-200/80 bg-white px-3 py-2 text-xs shadow-xl'>
            <p className='font-semibold text-slate-900'>{formatBarDayLabel(row.date)}</p>
            <p className='mt-1 text-slate-500'>
                Usuarios únicos:{' '}
                <span className='font-semibold tabular-nums text-slate-900'>
                    {users.toLocaleString('es-MX')}
                </span>
            </p>
        </div>
    );
};

const UniqueUsersBarCard = ({
    metrics, loading, period, onPeriodChange, reduceMotion,
}) => {
    const scrollRef = useRef(null);
    const windowStartRef = useRef(0);
    const initializedScrollRef = useRef(false);
    const [windowStart, setWindowStart] = useState(0);
    const [hoveredDate, setHoveredDate] = useState(null);
    const data = useMemo(
        () => metrics?.uniqueByDay || [],
        [metrics?.uniqueByDay],
    );
    const dataKey = useMemo(
        () => `${period}:${data.length}:${data[0]?.date || ''}:${data[data.length - 1]?.date || ''}`,
        [period, data],
    );
    const hasData = useMemo(
        () => data.some((row) => (row.users || 0) > 0),
        [data],
    );
    const totalUsers = metrics?.uniqueConnected ?? metrics?.uniqueConnectedToday ?? 0;
    const todayKey = mexicoTodayKey();
    const highlightDate = useMemo(() => {
        if (data.some((row) => row.date === todayKey)) return todayKey;
        return data[data.length - 1]?.date || null;
    }, [data, todayKey]);
    const needsScroll = data.length > BAR_VISIBLE_DAYS;
    const maxWindowStart = Math.max(0, data.length - BAR_VISIBLE_DAYS);
    const chartWidthPct = needsScroll
        ? (data.length / BAR_VISIBLE_DAYS) * 100
        : 100;
    const { peak, yMax } = useMemo(() => {
        let best = null;
        let maxUsers = 0;
        for (const row of data) {
            const users = row.users || 0;
            if (users > maxUsers) {
                maxUsers = users;
                best = row;
            }
        }
        return { peak: best, yMax: Math.max(1, maxUsers) };
    }, [data]);
    const yTicks = useMemo(() => buildIntegerTicks(yMax), [yMax]);

    windowStartRef.current = windowStart;

    const applyScrollToStart = useCallback((nextStart, { smooth = false } = {}) => {
        const node = scrollRef.current;
        if (!node || !needsScroll) return;
        const clamped = Math.max(0, Math.min(nextStart, maxWindowStart));
        const maxScroll = Math.max(0, node.scrollWidth - node.clientWidth);
        const left = maxWindowStart > 0 ? (clamped / maxWindowStart) * maxScroll : 0;
        if (smooth) {
            node.scrollTo({ left, behavior: 'smooth' });
        } else {
            node.scrollLeft = left;
        }
        setWindowStart(clamped);
        windowStartRef.current = clamped;
    }, [maxWindowStart, needsScroll]);

    const syncWindowFromScroll = useCallback(() => {
        const node = scrollRef.current;
        if (!node || !needsScroll) {
            setWindowStart(0);
            windowStartRef.current = 0;
            return;
        }
        const maxScroll = node.scrollWidth - node.clientWidth;
        if (maxScroll <= 0) {
            setWindowStart(0);
            windowStartRef.current = 0;
            return;
        }
        const next = Math.round((node.scrollLeft / maxScroll) * maxWindowStart);
        setWindowStart(next);
        windowStartRef.current = next;
    }, [maxWindowStart, needsScroll]);

    // Al cambiar periodo/datos: ir al final. Luego preservar ventana al redimensionar.
    useEffect(() => {
        initializedScrollRef.current = false;
    }, [dataKey]);

    useEffect(() => {
        const node = scrollRef.current;
        if (!node || !needsScroll) {
            setWindowStart(0);
            windowStartRef.current = 0;
            return undefined;
        }

        const frame = window.requestAnimationFrame(() => {
            if (!initializedScrollRef.current) {
                applyScrollToStart(maxWindowStart, { smooth: false });
                initializedScrollRef.current = true;
                return;
            }
            applyScrollToStart(windowStartRef.current, { smooth: false });
        });
        return () => window.cancelAnimationFrame(frame);
    }, [applyScrollToStart, dataKey, maxWindowStart, needsScroll]);

    useEffect(() => {
        const node = scrollRef.current;
        if (!node || !needsScroll) return undefined;

        const onWheel = (event) => {
            if (node.scrollWidth <= node.clientWidth) return;
            const delta = Math.abs(event.deltaY) > Math.abs(event.deltaX)
                ? event.deltaY
                : event.deltaX;
            if (!delta) return;
            event.preventDefault();
            node.scrollLeft += delta;
            syncWindowFromScroll();
        };

        node.addEventListener('wheel', onWheel, { passive: false });
        return () => node.removeEventListener('wheel', onWheel);
    }, [needsScroll, dataKey, syncWindowFromScroll]);

    useEffect(() => {
        const node = scrollRef.current;
        if (!node || !needsScroll) return undefined;

        const observer = new ResizeObserver(() => {
            applyScrollToStart(windowStartRef.current, { smooth: false });
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, [applyScrollToStart, needsScroll, dataKey]);

    return (
        <div className={cn(cardClass, 'pb-2')}>
            <div className='flex shrink-0 items-start justify-between gap-3'>
                <div className='min-w-0'>
                    <p className='text-base font-bold text-slate-900'>Usuarios únicos por día</p>
                    {loading ? (
                        <MetricSkeleton className='mt-1.5 h-9 w-20' />
                    ) : (
                        <div className='mt-1 flex flex-wrap items-start gap-x-5 gap-y-1'>
                            <p className='text-4xl font-bold tracking-tight tabular-nums text-slate-950 sm:text-5xl'>
                                {Number(totalUsers).toLocaleString('es-MX')}
                            </p>
                            <div className='mt-1.5 flex flex-wrap items-center gap-3 text-xs text-slate-500 sm:mt-2'>
                                <span className='inline-flex items-center gap-1.5'>
                                    <span className='size-2 rounded-full bg-violet-600' aria-hidden='true' />
                                    Hoy
                                </span>
                                <span className='inline-flex items-center gap-1.5'>
                                    <span className='size-2 rounded-full bg-slate-300' aria-hidden='true' />
                                    Otros días
                                </span>
                            </div>
                        </div>
                    )}
                </div>
                <PeriodCompactFilter value={period} onChange={onPeriodChange} disabled={loading} />
            </div>

            {loading ? (
                <div className='mt-3 flex min-h-[200px] flex-1 items-end gap-1.5'>
                    {Array.from({ length: 7 }).map((_, i) => (
                        <div
                            key={i}
                            className='w-full rounded-t-md bg-slate-200/90 animate-blink'
                            style={{ height: `${48 + (i % 4) * 18}px` }}
                            aria-hidden
                        />
                    ))}
                </div>
            ) : !hasData ? (
                <EmptyChart message='No hay usuarios únicos en este periodo.' />
            ) : (
                <div className='mt-2 flex min-h-0 flex-1 flex-col'>
                    {peak ? (
                        <p className='mb-1 shrink-0 text-center text-[11px] leading-tight text-slate-400'>
                            Pico: {formatBarDayLabel(peak.date)} · {peak.users} usuario{peak.users === 1 ? '' : 's'}
                        </p>
                    ) : null}
                    <div className='group/chart relative min-h-[200px] flex-1'>
                        <div className='absolute inset-0 flex min-h-0'>
                            <div className='w-10 shrink-0 self-stretch'>
                                <ChartContainer config={usersByDayConfig} className='h-full w-full aspect-auto'>
                                    <BarChart
                                        data={[{ users: yMax }]}
                                        margin={{ top: 4, right: 0, left: 0, bottom: 18 }}
                                    >
                                        <YAxis
                                            dataKey='users'
                                            domain={[0, yMax]}
                                            ticks={yTicks}
                                            allowDecimals={false}
                                            tickLine={false}
                                            axisLine={false}
                                            width={34}
                                            tick={{ fontSize: 11, fill: '#64748b' }}
                                            interval={0}
                                            minTickGap={0}
                                        />
                                        <XAxis dataKey='users' hide />
                                        <Bar dataKey='users' hide isAnimationActive={false} />
                                    </BarChart>
                                </ChartContainer>
                            </div>

                            <div
                                ref={scrollRef}
                                onScroll={syncWindowFromScroll}
                                className='min-w-0 flex-1 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
                            >
                                <div
                                    className='h-full'
                                    style={{ width: `${chartWidthPct}%`, minWidth: '100%' }}
                                >
                                    <ChartContainer config={usersByDayConfig} className='h-full w-full aspect-auto'>
                                        <BarChart
                                            data={data}
                                            margin={{ top: 4, right: 4, left: 2, bottom: 18 }}
                                            onMouseMove={(state) => {
                                                if (state?.isTooltipActive && state?.activeTooltipIndex != null) {
                                                    const row = data[state.activeTooltipIndex];
                                                    setHoveredDate(row?.date || null);
                                                } else {
                                                    setHoveredDate(null);
                                                }
                                            }}
                                            onMouseLeave={() => setHoveredDate(null)}
                                        >
                                            <defs>
                                                <pattern
                                                    id='usersBarHatch'
                                                    width='6'
                                                    height='6'
                                                    patternUnits='userSpaceOnUse'
                                                    patternTransform='rotate(45)'
                                                >
                                                    <rect width='6' height='6' fill='#f1f5f9' />
                                                    <line x1='0' y1='0' x2='0' y2='6' stroke='#cbd5e1' strokeWidth='2' />
                                                </pattern>
                                            </defs>
                                            <CartesianGrid vertical={false} strokeDasharray='4 4' stroke='#e2e8f0' />
                                            <YAxis
                                                domain={[0, yMax]}
                                                ticks={yTicks}
                                                hide
                                                allowDecimals={false}
                                            />
                                            <XAxis
                                                dataKey='label'
                                                tickLine={false}
                                                axisLine={false}
                                                tickMargin={4}
                                                height={18}
                                                tick={{ fontSize: 11, fill: '#64748b' }}
                                            />
                                            <ChartTooltip
                                                cursor={false}
                                                content={<UniqueUsersTooltip />}
                                            />
                                            <Bar
                                                dataKey='users'
                                                name='Usuarios únicos'
                                                shape={(props) => (
                                                    <StripedBar
                                                        {...props}
                                                        highlightDate={highlightDate}
                                                        hoveredDate={hoveredDate}
                                                        fill={CHART_ACCENT}
                                                    />
                                                )}
                                                isAnimationActive={!reduceMotion}
                                                animationDuration={600}
                                                maxBarSize={34}
                                            />
                                        </BarChart>
                                    </ChartContainer>
                                </div>
                            </div>
                        </div>

                        {needsScroll ? (
                            <>
                                <button
                                    type='button'
                                    aria-label='Ver días anteriores'
                                    disabled={windowStart <= 0}
                                    onClick={() => applyScrollToStart(windowStart - BAR_VISIBLE_DAYS, { smooth: true })}
                                    className='absolute left-1 top-1/2 z-10 inline-flex size-8 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white/95 text-slate-600 shadow-sm opacity-0 pointer-events-none transition hover:bg-white hover:text-slate-900 focus-visible:pointer-events-auto focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 group-hover/chart:pointer-events-auto group-hover/chart:opacity-100 group-focus-within/chart:pointer-events-auto group-focus-within/chart:opacity-100 disabled:opacity-0 group-hover/chart:disabled:opacity-35'
                                >
                                    <ChevronLeftIcon className='size-4' aria-hidden='true' />
                                </button>
                                <button
                                    type='button'
                                    aria-label='Ver días siguientes'
                                    disabled={windowStart >= maxWindowStart}
                                    onClick={() => applyScrollToStart(windowStart + BAR_VISIBLE_DAYS, { smooth: true })}
                                    className='absolute right-1 top-1/2 z-10 inline-flex size-8 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white/95 text-slate-600 shadow-sm opacity-0 pointer-events-none transition hover:bg-white hover:text-slate-900 focus-visible:pointer-events-auto focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 group-hover/chart:pointer-events-auto group-hover/chart:opacity-100 group-focus-within/chart:pointer-events-auto group-focus-within/chart:opacity-100 disabled:opacity-0 group-hover/chart:disabled:opacity-35'
                                >
                                    <ChevronRightIcon className='size-4' aria-hidden='true' />
                                </button>
                            </>
                        ) : null}
                    </div>
                </div>
            )}
        </div>
    );
};

export const UsuariosOnlineMetricCards = ({
    liveMetrics,
    liveLoading = false,
    roleMetrics,
    roleLoading = false,
    rolePeriod = DEFAULT_CHART_PERIOD,
    onRolePeriodChange,
    deviceMetrics,
    deviceLoading = false,
    devicePeriod = DEFAULT_CHART_PERIOD,
    onDevicePeriodChange,
    barMetrics,
    barLoading = false,
    barPeriod = DEFAULT_CHART_PERIOD,
    onBarPeriodChange,
}) => {
    const reduceMotion = usePrefersReducedMotion();
    const liveSeries = useLiveOnlineSeries(liveMetrics?.currentlyOnline, liveLoading);

    return (
        <div className='grid grid-cols-1 gap-3 lg:grid-cols-12 lg:items-stretch'>
            <div className='lg:col-span-4'>
                <ActivityKpis
                    metrics={liveMetrics}
                    loading={liveLoading}
                    liveSeries={liveSeries}
                    reduceMotion={reduceMotion}
                />
            </div>

            <div className='flex min-h-0 flex-col gap-3 lg:col-span-4'>
                <div className='min-h-0 flex-1'>
                    <RoleDonutCard
                        metrics={roleMetrics}
                        loading={roleLoading}
                        reduceMotion={reduceMotion}
                        period={rolePeriod}
                        onPeriodChange={onRolePeriodChange}
                    />
                </div>
                <div className='min-h-0 flex-1'>
                    <DeviceDonutCard
                        metrics={deviceMetrics}
                        loading={deviceLoading}
                        reduceMotion={reduceMotion}
                        period={devicePeriod}
                        onPeriodChange={onDevicePeriodChange}
                    />
                </div>
            </div>

            <div className='lg:col-span-4'>
                <UniqueUsersBarCard
                    metrics={barMetrics}
                    loading={barLoading}
                    period={barPeriod}
                    onPeriodChange={onBarPeriodChange}
                    reduceMotion={reduceMotion}
                />
            </div>
        </div>
    );
};
